import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { animated, config, easings, useSpring } from 'react-spring';
import styled, { CSSProperties } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { CardImg } from '@sorare/core/src/components/card/CardImg';

import {
  BaseLayout,
  CardOrPackRelativeWrapper,
  CardWrapper,
  TopClearance,
} from '../ui';
import packBottom from './assets/packBottom.png';
import topStripe from './assets/topStripe.png';

const Wrapper = styled(CardOrPackRelativeWrapper)`
  aspect-ratio: var(--pack-aspect-ratio);
`;
const PackWrapper = styled(animated.div)`
  position: absolute;
  top: 0;
  width: 100%;
  aspect-ratio: var(--pack-aspect-ratio);
  z-index: 1;
`;
const CardMovementWrapper = styled(animated.div)`
  width: 100%;
  position: relative;
  isolation: isolate;

  transform: translateY(calc(var(--translate-factor) * var(--stripe-height)));
`;
const PackBottomImg = styled.img.attrs({ src: packBottom })`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  transition: opacity ease-in 1s 0.5s;
`;
const TopStripeImg = styled.img.attrs({ src: topStripe })`
  position: absolute;
  width: 100%;
  transform: translate(0, 1px);
  clip-path: polygon(
    var(--progressPercentage) 0%,
    100% 0%,
    100% 100%,
    calc(var(--progressPercentage) - ((1 - var(--progress) / 100) * 50px)) 100%
  );
`;
const DanglingStripeWrapper = styled.div`
  width: 100%;
  height: var(--stripe-height);
  position: absolute;
  transform-origin: top left;
  overflow: hidden;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 50px 100%);
  opacity: 1;
  transition: opacity ease-in 1s 0.5s;
  transform: translate(var(--progressPercentage))
    rotate(calc((1 - var(--progress) / 300) * 90deg));
`;
const DanglingStripeImg = styled.img.attrs({ src: topStripe })`
  width: 100%;
  position: absolute;
  transform: rotateY(-180deg) translate(calc(100% - var(--progressPercentage)));
`;
const DragDiv = styled.div`
  position: absolute;
  inset: 0;
  touch-action: none;
`;
const DashedLineWrapper = styled(Vertical)`
  position: absolute;
  top: var(--stripe-height);
  width: 100%;
  overflow: hidden;
`;
const DashedLineSVG = styled(animated.svg)`
  width: 100%;
  height: 3px;
  line {
    stroke: var(--c-white);
    stroke-width: 3px;
    stroke-dasharray: 5% 2%;
  }
`;
const BrightEffectWrapper = styled.div`
  position: absolute;
  top: 0;
  opacity: 1;
  z-index: 1;

  &.opened {
    opacity: 0;
    transition: opacity linear 0.5s 0.45s;
  }
`;
const BrightEffect = styled.div`
  filter: brightness(15) blur(2px) drop-shadow(0 0 30px #ffffff);
  opacity: calc((var(--progress) - 30) / 100);

  &.opened {
    opacity: 1;
    transition: opacity linear 0.4s;
  }
`;
const ClipPath = styled.div`
  clip-path: rect(0 100% calc(100% - var(--stripe-height)) 0);
`;
const CardOpacityWrapper = styled.div`
  width: 100%;
  opacity: 0;

  &.opened {
    opacity: 1;
    transition: opacity linear 0.1s 0.2s;
  }
`;
const BottomArea = styled(Vertical).attrs({ center: true })`
  padding-top: var(--double-unit);
`;

type Props = {
  firstCardUrl: string;
  onDone: () => void;
};
export const PackTear = ({ firstCardUrl, onDone }: Props) => {
  const [pixelsTeared, setPixelsTeared] = useState(0);
  const [packOpened, setPackOpened] = useState(false);
  const [verticalMovementStarted, setVerticalMovementStarted] = useState(false);
  const packRef = useRef<HTMLDivElement>(null);

  const [tearAnimationSpring, tearAnimationSpringApi] = useSpring(() => ({
    '--progress': 0,
    '--progressPercentage': '0%',
  }));
  const animateProgress = (progress: number) => {
    tearAnimationSpringApi.start({
      '--progress': progress,
      '--progressPercentage': `${progress}%`,
    });
  };

  const [dashedLineStyle] = useSpring(() => ({
    from: {
      transform: `translate(-100%)`,
    },
    to: {
      transform: `translate(100%)`,
    },
    loop: true,
    config: config.slow,
  }));
  const [packDownSpring, packDownApi] = useSpring(() => ({
    transform: 'translateY(0px) rotate(0deg)',
  }));
  const [verticalMovementStyle, verticalMovementApi] = useSpring(() => ({
    '--translate-factor': 1,
  }));

  if (packOpened && !verticalMovementStarted) {
    verticalMovementApi.start({
      '--translate-factor': -1,
      config: { duration: 400, easing: easings.easeInOutQuad },
      onRest: () => {
        verticalMovementApi.start({
          '--translate-factor': 0,
          config: { duration: 500, easing: easings.easeInOutQuad },
          delay: 50,
          onRest: () => {
            onDone();
          },
        });
      },
    });
    setVerticalMovementStarted(true);
  }

  const movePackDown = () => {
    packDownApi.start({
      transform: 'translateY(700px) rotate(5deg)',
      config: { duration: 600, easing: easings.easeInQuad },
    });
  };
  const fullyTear = () => {
    animateProgress(120);
    setPackOpened(true);
    movePackDown();
  };

  const bind = useDrag(
    ({ xy: [x], delta: [xDelta], initial: [gestureStartPosition], last }) => {
      if (packRef.current) {
        const { left: packLeftCoordinate, width } =
          packRef.current?.getBoundingClientRect() || {};
        const isMakingProgress = xDelta > 0 && x > pixelsTeared;
        const gestureStartsInAuthorizedZone =
          pixelsTeared === 0
            ? gestureStartPosition < packLeftCoordinate + width / 2
            : true;

        if (isMakingProgress && gestureStartsInAuthorizedZone) {
          const progress = (100 * (x - packLeftCoordinate)) / width;
          if (progress < 120) {
            animateProgress(progress);
          }
          if (progress >= 95 && !packOpened) {
            setPackOpened(true);
            movePackDown();
          }
        }

        if ((last || xDelta < 0) && x > pixelsTeared) {
          setPixelsTeared(x);
        }
      }
    }
  );

  return (
    <animated.div style={tearAnimationSpring as CSSProperties}>
      <BaseLayout>
        <TopClearance>
          <LabelL bold>
            <FormattedMessage
              id="OpenPack.swipe"
              defaultMessage="Swipe to open"
            />
          </LabelL>
        </TopClearance>
        <Wrapper>
          <PackWrapper ref={packRef} style={packDownSpring}>
            <PackBottomImg />
            <TopStripeImg />
            <DanglingStripeWrapper>
              <DanglingStripeImg />
            </DanglingStripeWrapper>
            {pixelsTeared === 0 && (
              <DashedLineWrapper>
                <DashedLineSVG style={dashedLineStyle}>
                  <line x1="0" y1="1.5" x2="40%" y2="1.5" />
                </DashedLineSVG>
              </DashedLineWrapper>
            )}
            <DragDiv {...bind()} />
          </PackWrapper>
          <CardWrapper>
            <CardMovementWrapper style={verticalMovementStyle as CSSProperties}>
              <CardOpacityWrapper
                className={classNames({ opened: packOpened })}
              >
                <CardImg width={320} src={firstCardUrl} draggable={false} />
              </CardOpacityWrapper>
              <BrightEffectWrapper
                className={classNames({ opened: packOpened })}
              >
                <BrightEffect className={classNames({ opened: packOpened })}>
                  <ClipPath>
                    <CardImg width={320} src={firstCardUrl} draggable={false} />
                  </ClipPath>
                </BrightEffect>
              </BrightEffectWrapper>
            </CardMovementWrapper>
          </CardWrapper>
        </Wrapper>
        <BottomArea>
          <Button size="medium" color="tertiary" onClick={fullyTear}>
            <FormattedMessage id="OpenPack.open" defaultMessage="Open" />
          </Button>
          <LabelM className="text-center">
            Disclaimer: the pack image is not correct. This will be fixed in a
            future release
          </LabelM>
        </BottomArea>
      </BaseLayout>
    </animated.div>
  );
};
